import { Component, OnInit } from '@angular/core';
import { TitleService } from 'src/app/shared/services/title.service';
import { Subscription, EMPTY, of, Observable, from, concat } from 'rxjs';
import { expand, map, mapTo, filter, pluck } from 'rxjs/operators';
import { FilePortalDataService } from '../file-portal-dataservice';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { matchClientPartyRow } from '../../shared/classes/helpers';

@Component({
  selector: 'e2cb-file-portal',
  templateUrl: './file-portal.component.html',
  styleUrls: ['./file-portal.component.scss']
})
export class FilePortalComponent implements OnInit {

  // #region Public Variables
  matchClientPartyRow = matchClientPartyRow;
  showSpinner: boolean;
  aggregationResultList: any[] = [];
  public rows: any[] = [];
  form: FormGroup;
  months = [
    { id: 1,
      name: 'January'
    }, {
      id: 2,
      name: 'February'
    }, {
      id: 3,
      name: 'March'
    }, {
      id: 4,
      name: 'April'
    }, {
      id: 5,
      name: 'May'
    }, {
      id: 6,
      name: 'June'
    }, {
      id: 7,
      name: 'July'
    }, {
      id: 8,
      name: 'August'
    }, {
      id: 9,
      name: 'September'
    }, {
      id: 10,
      name: 'October'
    }, {
      id: 11,
      name: 'November'
    }, {
      id: 12,
      name: 'December'
    }
  ];
  years: number[] = [];
  hasLookedUpClient: boolean = false;
  currentDate = new Date();
  get uploadEnabled(): boolean {
    let selectedMonth = this.form.value.month - 1;
    let selectedYear = this.form.value.year;
    let selectedDate = new Date();
    selectedDate.setFullYear(selectedYear);
    selectedDate.setMonth(selectedMonth);
    return this.currentDate > selectedDate && this.hasLookedUpClient;
  }
  // #endregion

  // --------------------------------------------------

  // #region Private Variables
  private _unsortedClientList: any[] = [];
  private _sfClientRegistryGetAllSub: Subscription;
  private _invoicesByClientIdSub: Subscription;
  private _clientBackupFilesByClientSub: Subscription;
  private _invoicesByClientIdInBlobSub: Subscription;
  // #endregion

  // --------------------------------------------------

  // #region ReadOnly Variables
  columns: any[] = [
    { name: 'File Name', prop: 'fileName'},
    // { name: 'Billing Period', prop: 'billingPeriod'},
    { name: 'Type', prop: 'fileType'}
  ];
  rowHeight = 50;
  selected = [];
  // #endregion

  // --------------------------------------------------

  // #region Constructors
  constructor(
    private _titleService: TitleService,
    public sharedDataService: SharedDataService,
    private _dataService: FilePortalDataService,
    private _fb: FormBuilder
  ) {

    this._titleService.Subtitle = 'File Portal';
    this.initializeYears();
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const prevMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    this.form = this._fb
      .group({
        clientId: ['', Validators.required],
        month: [prevMonth, Validators.required],
        year: [currentYear, Validators.required]
      });

    this.form.valueChanges.subscribe(() => {
      this.rows = [];
      this.hasLookedUpClient = false;
    });
  }
  // #endregion

  // --------------------------------------------------

  // #region Public Methods
  async ngOnInit() {
    this.showSpinner = true;
    try {
      await this.sharedDataService.PopulateAviClientIds();
    } catch (error) {
      this.sharedDataService.HandleError(error);
      this.showSpinner = false;
    } finally {
      this.showSpinner = false;
    }
  }

// #engregion

// --------------------------------------------------

// #region Public Methods
  private getClientInvoiceFileInfo(selectedId: string): void {
    this.showSpinner = true;
    if(this._invoicesByClientIdSub)
    {
      this._invoicesByClientIdSub.unsubscribe();
    }
    this._invoicesByClientIdSub = this._dataService.getInvoicesByClientId(selectedId).pipe(
    expand(res =>
    {
      // check for errors
      if (!res.success)
      {
        this.handleError(res.errors);
        return EMPTY;
      }
      // make sure we have data
      if (!res.data || !res.data.results)
      {
        this.showSpinner = false;
        return EMPTY;
      }

      // add the data (if any)
      const results = res.data.results;
      results.forEach(result =>
      {
        result.invoicePdfAttachmentIDs.forEach(attachmentId =>
        {
          const invAttachment =  this._dataService.getAttachment(attachmentId).subscribe(innerRes =>
          {
            if (!innerRes.success)
            {
              this.sharedDataService.HandleError(innerRes.errors);
            }
            if(innerRes.data)
            {
              this.rows.push({
              fileName: innerRes.data.fileName,
              billingPeriod: result.month + '/' + result.year,
              fileType: 'Usage',
              mimeType: innerRes.data.mimeType,
              attachmentId: innerRes.data.attachmentId
              });
              console.log("rows updated");
              this.rows = [...this.rows];
            }
          });
        }); // end results foreach
        // don't recurse if there's no continuation token.
      });

      if (!res.data.continuationToken)
      {
        this.showSpinner = false;
        return EMPTY;
      }
      return this._dataService.getInvoicesByClientId(res.data.continuationToken);
    })).subscribe();
  }

  private getAllFileNamesForClientInBlob(model: any): void {
    this.showSpinner = true;
    if (this._invoicesByClientIdInBlobSub) {
      this._invoicesByClientIdInBlobSub.unsubscribe();
    }
    this._invoicesByClientIdInBlobSub = this._dataService.getAllFileNamesForClientInBlob(model).pipe(
      expand(res => {
        this.hasLookedUpClient = true;

        if (!res || !res.length) {
          this.showSpinner = false;
          return EMPTY;
        }

        // add the data (if any)
        const results = res;
        results.forEach(result => {
          this.rows.push({
            fileName: result,
            fileType: 'Usage',
          });
          this.rows = [...this.rows];
        });

        this.showSpinner = false;
      })).subscribe();
  }

  initializeYears() {
    const today = new Date();
    const minYear = today.getFullYear();

    for (let i = minYear; i > (minYear - 10); i--) {
      this.years.push(i);
    }
  }

  handleSelect(event) {
    // only listen to click events.
    if (event.type !== 'click' || this.selected.length !== 1)
    {
      return;
    }
    // stop the propagation of the event.
    this.showSpinner = true;
    event.cellElement.blur();
    // get the current aggregation while clearing the selected.
    const selectedRow = this.selected;
    this._dataService.downloadFileFromBlob(selectedRow[0].fileName)
    .pipe(map((res) => {
      return {
        filename: selectedRow[0].fileName,
        data: new Blob(
          [res['body']],
          { type: selectedRow[0].mimeType}
        ),
      };
    }))
    .subscribe(res =>
    {
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(res.data, res.filename);
      }
      else
      {
        const link = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = link;
        a.download = res.filename;
        a.click();
        window.URL.revokeObjectURL(link);
        a.remove();
      }
    }, error => {
      this.handleError(error);
    }, () => {
      this.showSpinner = false;
    });
  }

  // ----------------------------------------------------------

  populateTableData() {
    if (this.form.valid) {
      const model = this.form.value;
      if (this.rows.length > 0) {
        this.rows.length = 0;
        this.rows = [];
      }
      this.getAllFileNamesForClientInBlob(model);
    }
  }

  // ----------------------------------------------------------

  private handleError(error: any): void {
    alert('An error occurred. Please see console log.');
    console.log(error);

    this.showSpinner = false;
  }

  // ----------------------------------------------------------

  private getClientBillingBackupFileInfo(selectedId): void {
    this.showSpinner = true;
    if(this._clientBackupFilesByClientSub)
    {
      this._clientBackupFilesByClientSub.unsubscribe();
    }
    this._clientBackupFilesByClientSub = this._dataService.getBackUpFilesByClientId(selectedId).pipe(
    expand(res =>
    {
      // check for errors
      if (!res.success)
      {
        this.sharedDataService.HandleError(res.errors);
        return EMPTY;
      }
      // make sure we have data
      if (!res.data || !res.data.results)
      {
        this.showSpinner = false;
        return EMPTY;
      }
      // add the data (if any)
      const results = res.data.results;
      results.forEach(result =>
        {
          const backUpattachment =  this._dataService.getAttachment(result.backupFileAttachmentId).subscribe(innerRes =>
          {
            if (!innerRes.success)
            {
              this.sharedDataService.HandleError(innerRes.errors);
            }
            if(innerRes.data)
            {
              this.rows.push({
              fileName: innerRes.data.fileName,
              billingPeriod: result.month + '/' + result.year,
              fileType: 'Usage',
              mimeType: innerRes.data.mimeType,
              attachmentId: innerRes.data.attachmentId
              });
              this.rows = [...this.rows];
            }
          });
        }); // end results foreach
      // don't recurse if there's no continuation token.
      if (!res.data.continuationToken)
      {
        this.showSpinner = false;
        return EMPTY;
      }
      return this._dataService.getBackUpFilesByClientId(selectedId, res.data.continuationToken);
    })).subscribe();
  }
  // #endregion
}
